import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import "./ticket.css";
import Swal from "sweetalert2";

const today = new Date().toISOString().split("T")[0];
const now = new Date();

const getCurrentDateTime = () =>
  new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);

const CreateTicket = () => {

   useEffect(() => {
    document.title = "Create Ticket | Oriflamme IT Solutions";
  }, []);

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    company_id: "",
    branch_id: "",
    complain_type_id: "",
    engineer_id: "",
    priority_id: "",
    complain_source_id: "",
    subject: "",
    description: "",
    start_date: getCurrentDateTime(),
    end_date: `${today}T19:00`,
  });

  const [formErrors, setFormErrors] = useState({});
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [complainTypes, setComplainTypes] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [complainSources, setComplainSources] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCreateData();
  }, []);

  const fetchCreateData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/complain-create-details`, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      if (res.status === 401) {
        navigate("/login");
        return;
      }

      const data = await res.json();

      setCompanies(data.companies || []);
      setBranches(data.branches || []);
      setComplainTypes(data.complain_types || []);
      setEngineers(data.engineers || []);
      setPriorities(data.priorities || []);
      setComplainSources(data.complain_sources || []);
    } catch (error) {
      console.log(error);
    }
  };

  const companyOptions = companies.map((c) => ({
    value: c.id,
    label: c.company_name,
  }));

  const branchOptions = filteredBranches.map((b) => ({
    value: b.id,
    label: b.branch_name,
  }));

  const complainTypeOptions = complainTypes.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const engineerOptions = engineers.map((e) => ({
    value: e.id,
    label: e.full_name,
  }));

  const priorityOptions = priorities.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const complainSourceOptions = complainSources.map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });

    setFormErrors((prev) => ({
      ...prev,
      [e.target.name]: null,
    }));
  };

  const handleCompanyChange = (selected) => {
    const companyId = selected ? selected.value : "";

    setFormData((prev) => ({
      ...prev,
      company_id: companyId,
      branch_id: "",
    }));

    const filtered = branches.filter(
      (branch) => branch.company_id == companyId,
    );

    setFilteredBranches(filtered);
  };

  const handleSelectChange = (field, selected) => {
    setFormData((prev) => ({
      ...prev,
      [field]: selected ? selected.value : "",
    }));

    setFormErrors((prev) => ({
      ...prev,
      [field]: null,
    }));
  };

  const showError = (field) => {
    if (!formErrors[field]) return null;

    return (
      <small className="text-danger">
        {Array.isArray(formErrors[field])
          ? formErrors[field][0]
          : formErrors[field]}
      </small>
    );
  };

  const resetForm = () => {
    setFormData({
      company_id: "",
      branch_id: "",
      complain_type_id: "",
      engineer_id: "",
      priority_id: "",
      complain_source_id: "",
      subject: "",
      description: "",
      start_date: getCurrentDateTime(),
      end_date: `${today}T19:00`,
    });

    setFilteredBranches([]);
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    setFormErrors({});

    try {
      const res = await fetch(`${API_URL}/api/complains`, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (res.status === 401) {
        navigate("/login");
        return;
      }

      const data = await res.json();

      if (res.ok) {
        Swal.fire({
          icon: "success",
          title: "Ticket Created Successfully!",
          text: "Your ticket has been created successfully.",
        });
        resetForm();
        navigate("/ticket");
      } else if (res.status === 422) {
        setFormErrors(data.errors || {});
      } else {
        alert(data.message || "Something went wrong");
      }
    } catch (error) {
      console.log(error);
      alert("Server error");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="container pt-4">
      <h3>Create Ticket</h3>

      <form onSubmit={handleSubmit} className="ticket-card p-4">
        <div className="row">
          <div className="col-md-6 mb-3">
            <label>Company*</label>
            <Select
              options={companyOptions}
              value={
                companyOptions.find(
                  (option) => option.value === formData.company_id,
                ) || null
              }
              onChange={handleCompanyChange}
              placeholder="Select Company"
            />
            {showError("company_id")}
          </div>

          <div className="col-md-6 mb-3">
            <label>Branch</label>
            <Select
              options={branchOptions}
              value={
                branchOptions.find(
                  (option) => option.value === formData.branch_id,
                ) || null
              }
              onChange={(selected) => handleSelectChange("branch_id", selected)}
              placeholder="Select Branch"
            />
            {showError("branch_id")}
          </div>

          <div className="col-md-6 mb-3">
            <label>Issue Type*</label>
            <Select
              options={complainTypeOptions}
              onChange={(selected) =>
                handleSelectChange("complain_type_id", selected)
              }
              value={complainTypeOptions.find(
                (option) => option.value === formData.complain_type_id,
              ) || null}
              placeholder="Select Issue Type"
            />
            {showError("complain_type_id")}
          </div>

          <div className="col-md-6 mb-3">
            <label>Assign To Engineer*</label>
            <Select
              options={engineerOptions}
              onChange={(selected) =>
                handleSelectChange("engineer_id", selected)
              }
              value={engineerOptions.find(
                (option) => option.value === formData.engineer_id,
              ) || null}
              placeholder="Select Engineer"
            />
            {showError("engineer_id")}
          </div>

          <div className="col-md-6 mb-3">
            <label>Priority*</label>
            <Select
              options={priorityOptions}
              onChange={(selected) =>
                handleSelectChange("priority_id", selected)
              }
              value={priorityOptions.find(
                (option) => option.value === formData.priority_id,
              ) || null}
              placeholder="Select Priority"
            />
            {showError("priority_id")}
          </div>

          <div className="col-md-6 mb-3">
            <label>Source</label>
            <Select
              options={complainSourceOptions}
              onChange={(selected) =>
                handleSelectChange("complain_source_id", selected)
              }
              value={complainSourceOptions.find(
                (option) => option.value === formData.complain_source_id,
              ) || null}
              placeholder="Select Source"
            />
            {showError("complain_source_id")}
          </div>

          <div className="col-md-6 mb-3">
            <label>Start*</label>
            <input
              type="datetime-local"
              className="form-control"
              name="start_date"
              value={formData.start_date}
              onChange={handleInputChange}
            />
            {showError("start_date")}
          </div>

          <div className="col-md-6 mb-3">
            <label>End*</label>
            <input
              type="datetime-local"
              className="form-control"
              name="end_date"
              value={formData.end_date}
              onChange={handleInputChange}
            />
            {showError("end_date")}
          </div>

          <div className="col-md-6 mb-3">
            <label>Subject*</label>
            <input
              type="text"
              className="form-control"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
            />
            {showError("subject")}
          </div>

          <div className="col-md-6 mb-3">
            <label>Description*</label>
            <textarea
              className="form-control"
              rows="2"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
            />
            {showError("description")}
          </div>
        </div>

        <div className="d-flex justify-content-end mb-3 pt-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary d-flex align-items-center gap-2"
          >
            {isSubmitting && (
              <span className="spinner-border spinner-border-sm"></span>
            )}
            {isSubmitting ? "Processing..." : "Save Ticket"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTicket;
